import React from 'react';
import { useHydratedTranslation } from '@util/hooks/useHydratedTranslation';
import PropTypes from 'prop-types';
import '@i18n/i18n';

import '../styles/tailwind.css';

import styled, { ThemeProvider } from 'styled-components';
import Header from './header';
import PageHeader from '@ui/PageHeader';
import Seo from '@components/seo';
import EnvironmentBanner, {
  BANNER_HEIGHT,
  IS_BANNER_VISIBLE,
} from '@components/EnvironmentBanner';
import siteMetadata from '@config/siteMetadata';

import { loadingStatus, useUser } from '@util/providers/AuthProvider';
import SignedOutElsewhere from '@components/Account/Modal/SignedOutElsewhere';

//* debug info
import ResponsiveHelper from '../debug/ResponsiveHelper';

const Layout = ({ children }) => {
  const { userStatus, signedOutElsewhere, dismissSignedOutElsewhere } =
    useUser();
  const isRetrievingUserInfo = userStatus !== loadingStatus.DONE;
  return (
    <ThemeProvider
      theme={{
        brand: {
          primary: '#00af66',
        },
      }}
    >
      <PageWrapper>
        <EnvironmentBanner />
        <SignedOutElsewhere
          isOpen={signedOutElsewhere}
          toggle={dismissSignedOutElsewhere}
        />
        <Header
          siteTitle={siteMetadata.title}
          isRetrievingUserInfo={isRetrievingUserInfo}
        />
        <MainContentContainer className="content pb-6 lg:pb-12 mt-0">
          <main className="mx-auto w-full px-3.75 sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl 2xl:max-w-2xl">
            {isRetrievingUserInfo ? <LoadingUser /> : children}
          </main>
        </MainContentContainer>
        <Footer
          className="pt-12 md:hidden"
          style={{
            marginBottom: 150,
          }}
        >
          <FooterContent mobile />
        </Footer>
        <Footer className="py-12 hidden md:block">
          <FooterContent />
        </Footer>
      </PageWrapper>
    </ThemeProvider>
  );
};

function FooterContent({ mobile = false }) {
  const { t } = useHydratedTranslation();

  const linkSpanCls = 'inline-block mx-4';
  const linkCls = 'text-primary hover:text-primary hover:underline';
  const tid = (name) => (mobile ? `Mobile${name}` : name);

  return (
    <>
      <div className="mx-auto w-full px-3.75 sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl 2xl:max-w-2xl">
        <span className={linkSpanCls}>
          <a
            data-testid={tid('FooterContactUs')}
            href={process.env.CONTACT_US}
            target="_blank"
            rel="noopener noreferrer"
            className={linkCls}
          >
            {t('legal.contactUs')}
          </a>
        </span>
        <span className={linkSpanCls}>
          <a
            data-testid={tid('FooterPrivacyPolicy')}
            href="https://www.tobiidynavox.com/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            className={linkCls}
          >
            {t('legal.privacyPolicy')}
          </a>
        </span>
        <span className={linkSpanCls}>
          <a
            data-testid={tid('FooterStudentDataPolicy')}
            href="https://us.tobiidynavox.com/pages/student-data-policy"
            target="_blank"
            rel="noopener noreferrer"
            className={linkCls}
          >
            {t('legal.studentPolicy')}
          </a>
        </span>
        <span className={linkSpanCls}>
          <a
            data-testid={tid('FooterTermsAndConditions')}
            href="https://download.mytobiidynavox.com/Forms_Warranties_Declarations/TobiiDynavox_Boardmaker_Terms_and_Conditions.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className={linkCls}
          >
            {t('legal.termsAndConditions')}
          </a>
        </span>
        <span className={`${linkSpanCls} text-gray-600`}>
          &copy; Tobii Dynavox, LLC {new Date().getFullYear()}
        </span>
      </div>

      <DebugFooter className="mt-1 text-gray-600">
        v.{`#{Build.BuildNumber}#`}
        {process.env.RESPONSIVE_VIEW_DEBUGGER === 'enabled' && (
          <ResponsiveHelper />
        )}
      </DebugFooter>
    </>
  );
}

function LoadingUser() {
  const { t } = useHydratedTranslation();
  return (
    <>
      <Seo title={`${t('account.loadingUser')}...`} />
      <div className="grid grid-cols-12 gap-x-8">
        <div className="col-span-full">
          <PageHeader>{t('account.loadingUser')}...</PageHeader>
        </div>
      </div>
    </>
  );
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
};

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const MainContentContainer = styled.div`
  padding-top: calc(${IS_BANNER_VISIBLE ? BANNER_HEIGHT : 0}px + 6rem);
  flex: 1;
`;

const Footer = styled.footer`
  text-align: center;
  background-color: #d9d9d6;
  border-top: 1px solid #e6e7e8;
`;

const DebugFooter = styled.div`
  font-size: 0.875rem;
  border-radius: 0;
  margin-bottom: 0;

  .debug-links {
    display: inline-block;
    margin-left: 2rem;
  }
`;

export default Layout;
